import { defineEvent, withPii } from "@amtana/module";
import { z } from "zod";

import { erasedBySchema, requestedViaSchema } from "./account.schemas.ts";

// Account's own erasure facts (ADR-026, epic 30, S30.01): one stream per person
// (`account:<personId>`). Self-service keeps today's 14-day grace (D805): `erasure_requested`
// is appended the moment it starts, `erasure_cancelled` the moment signing back in restores it.
// `erasure_initiated` is appended the instant the gate clears for real — self-service once the
// grace elapses, staff immediately — before anything is purged anywhere (ADR-026 point 3): it
// carries the still-live email and language so S30.13's own confirmation delivery can reach an
// address that, by send time, may already be gone. Only once that delivery settles (or its own
// bounded window lapses) does the Go `erasure` process manager fan one worker-bridge `erase-step`
// intent out per module (D806); account's own door purges the row and appends `person_erased`
// (ids only) in the same transaction. `erasure_step_failed` is a dead step, module named, the
// same shape `notification.delivery_failed` already takes (ADR-022 point 8).

export const ACCOUNT = "account";

export const erasureRequested = withPii(
  defineEvent(
    ACCOUNT,
    "erasure_requested",
    z.object({
      personId: z.string(),
      email: z.string(),
      language: z.string(),
      purgeAt: z.string(),
    }),
  ),
  ["email"],
);

export const erasureCancelled = withPii(
  defineEvent(
    ACCOUNT,
    "erasure_cancelled",
    z.object({ personId: z.string(), email: z.string(), language: z.string() }),
  ),
  ["email"],
);

/** Carries `email`/`language` still live (ADR-026 consequences: the one deliberate exception to
 * "a fact carries ids only from this epic on," since its own address is the one this epic is
 * about to erase). `staffId`/`reason` only when staff acted; `requestedVia` only when staff was
 * given the channel the person asked through (never `reason` itself, which is never echoed back,
 * D810). */
export const erasureInitiated = withPii(
  defineEvent(
    ACCOUNT,
    "erasure_initiated",
    z.object({
      personId: z.string(),
      email: z.string(),
      language: z.string(),
      erasedBy: erasedBySchema,
      staffId: z.string().optional(),
      requestedVia: requestedViaSchema.optional(),
      /** The person's sole/personal organization ids (D879-D881, billing ordering fix), computed
       * before anything is purged anywhere (the self-service gate, or staff's own door) and
       * carried forward onto `erasure_notify_settled` the same way `email` already is (D828),
       * so every module's own `erase-step` intent that needs "which organizations does this
       * person own alone" never has to ask organization again at a point where its own row may
       * already be gone. Optional/defaulted: an older, already-appended fact carries no key at
       * all and reads as none. */
      organizationIds: z.array(z.string()).optional().default([]),
    }),
  ),
  ["email"],
);
// `reason` stays off this fact (review group 2, decision g): free text, kept only in the
// account row's own audit-only `erasure.reason` field, read back by `eraseStep` to carry
// `erasedBy`/`staffId` forward onto `person_erased` (D817) but never appended to an event the
// log keeps forever, never re-echoed, and that erasure's own redaction sweep (S30.09) would
// otherwise have to go looking for free text in.

/** Ids only (ADR-026 point 3): appended beside account's own row purge, in the same transaction. */
export const personErased = defineEvent(
  ACCOUNT,
  "person_erased",
  z.object({
    personId: z.string(),
    erasedBy: erasedBySchema,
    staffId: z.string().optional(),
    reason: z.string().optional(),
    erasedAt: z.string(),
  }),
);

/** A module's `erase-step` door spent every retry, or the confirmation delivery's own 24-hour
 * window lapsed with nothing sent (`module: "notify"`, ADR-026 point 9, D809). Reaches `#ops`
 * the same way `notification.delivery_failed` does, and is replayable from `/ops` beside dead
 * deliveries and dead intents (S26.10). */
export const erasureStepFailed = defineEvent(
  ACCOUNT,
  "erasure_step_failed",
  z.object({
    personId: z.string(),
    module: z.string(),
    attempts: z.number().int().nonnegative(),
    lastFailureCode: z.string().nullable(),
  }),
);

/** The erasure managers' own choreography (ADR-026 point 3/9): the `erasure/pm/notify` manager
 * has account append this once the confirmation delivery has settled, sent or given up on after
 * its own 24-hour window (D809), through `AccountApi.settleErasureNotify` since the managers run
 * in worker-bridge (epic 44, S44.07, D1392); the Go worker appends it itself until the cutover.
 * Either way, the `erasure/pm/fanout` manager hears it on the same stream and fans the module
 * `erase-step` intents out, so both managers keep the same crash-safe, event-sourced shape every
 * other pipeline here already does, rather than one calling the other's state transition.
 *
 * Carries `email` too (S30.06, D828): `erasure_requested`'s own state (what the fan-out used to
 * read this from) is self-service only, so a staff-driven, grace-less erasure, which never
 * states that fact, left the fan-out with nothing to carry an email forward from. `notify`
 * already has it, off `erasure_initiated` itself, for every path alike. */
export const erasureNotifySettled = withPii(
  defineEvent(
    ACCOUNT,
    "erasure_notify_settled",
    z.object({
      personId: z.string(),
      email: z.string(),
      /** Carried forward from `erasure_initiated` (D879-D881, billing ordering fix), the same way
       * `email` already is: the fan-out reads this straight off this fact to build every
       * module's own `erase-step` intent, never a fresh peer call to organization that could
       * race its own purge. */
      organizationIds: z.array(z.string()).optional().default([]),
    }),
  ),
  ["email"],
);

export const accountEvents = [
  erasureRequested,
  erasureCancelled,
  erasureInitiated,
  personErased,
  erasureStepFailed,
  erasureNotifySettled,
] as const;
